<template>
  <div class="tminus" :class="phaseClass">
    <div class="tminus__label">{{ effectiveConfig.label || 'Countdown' }}</div>
    <div class="tminus__display">
      <span class="tminus__prefix">{{ prefix }}</span>
      <span class="tminus__time">{{ clock }}</span>
    </div>
    <div v-if="phase === 'go'" class="tminus__go">
      <i class="fas fa-rocket"></i> GO
    </div>
    <div v-if="mode === 'duration' || usesService" class="tminus__controls btn-group btn-group-sm">
      <button
        type="button"
        class="btn"
        :class="isRunning ? 'btn-warning' : 'btn-success'"
        :disabled="phase === 'go' && !allowOvertime"
        @click="toggle"
      >
        {{ isRunning ? 'Pause' : 'Start' }}
      </button>
      <button type="button" class="btn btn-secondary" @click="reset">Reset</button>
    </div>
  </div>
</template>

<script>
import serviceLinkMixin from '@/widgets/serviceLinkMixin'

export default {
  mixins: [serviceLinkMixin],
  props: {
    config: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      remaining: 0,
      running: false,
      timer: null,
      goHoldUntil: 0,
      targetAt: 0,
    }
  },
  computed: {
    effectiveConfig() {
      if (!this.usesService || !this.activeService) return this.config
      return {
        ...this.config,
        label: this.activeService.tMinusLabel || this.activeService.name,
        mode: this.activeService.tMinusMode || 'duration',
        duration: this.activeService.tMinusDuration,
        targetTime: this.activeService.tMinusTargetTime,
      }
    },
    mode() {
      return this.effectiveConfig.mode === 'wallclock' ? 'wallclock' : 'duration'
    },
    allowOvertime() {
      return this.effectiveConfig.overtime !== false
    },
    isRunning() {
      if (this.usesService) return this.serviceSessionRunning
      return this.running
    },
    currentRemaining() {
      if (this.usesService) return this.serviceCountdownRemaining
      return this.remaining
    },
    phase() {
      if (this.currentRemaining > 0) return 'countdown'
      if (this.goHoldUntil && Date.now() < this.goHoldUntil) return 'go'
      if (this.currentRemaining === 0 || (!this.allowOvertime && this.currentRemaining <= 0)) return 'go'
      return 'over'
    },
    phaseClass() {
      return `tminus--${this.phase}`
    },
    prefix() {
      if (this.phase === 'go') return ''
      return this.currentRemaining >= 0 ? 'T−' : 'T+'
    },
    clock() {
      if (this.phase === 'go') return ''
      const s = Math.abs(this.currentRemaining)
      const h = Math.floor(s / 3600)
      const m = Math.floor((s % 3600) / 60)
      const sec = s % 60
      if (h > 0) {
        return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
      }
      return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
    },
  },
  watch: {
    'effectiveConfig.duration'() {
      if (this.usesService) return
      if (this.mode === 'duration' && !this.running) {
        this.remaining = this.initialSeconds()
      }
    },
    'effectiveConfig.targetTime'() {
      if (this.usesService) return
      if (this.mode === 'wallclock') this.armWallclock()
    },
    'effectiveConfig.mode'(mode) {
      this.goHoldUntil = 0
      if (this.usesService) return
      if (mode === 'wallclock') this.armWallclock()
      else {
        this.pause()
        this.targetAt = 0
        this.remaining = this.initialSeconds()
      }
    },
    serviceCountdownRemaining(val, oldVal) {
      if (!this.usesService) return
      if (oldVal > 0 && val <= 0) {
        this.goHoldUntil = Date.now() + 4000
      }
      if (this.goHoldUntil && Date.now() >= this.goHoldUntil) {
        this.goHoldUntil = 0
      }
    },
    activeServiceId() {
      this.goHoldUntil = 0
    },
  },
  created() {
    if (this.usesService) return
    if (this.mode === 'wallclock') this.armWallclock(false)
    else this.remaining = this.initialSeconds()
  },
  mounted() {
    if (!this.usesService && this.mode === 'wallclock') this.ensureWallclockRunning()
  },
  beforeDestroy() {
    clearInterval(this.timer)
  },
  methods: {
    initialSeconds() {
      if (this.effectiveConfig.mode === 'wallclock') {
        return this.secondsUntilTarget(this.effectiveConfig.targetTime || '10:00')
      }
      const n = Number(this.effectiveConfig.duration)
      return Number.isFinite(n) && n > 0 ? Math.round(n) : 600
    },
    resolveTargetAt(hhmm) {
      const m = String(hhmm || '10:00').match(/^(\d{1,2}):(\d{2})$/)
      const hour = m ? Math.min(23, Math.max(0, Number(m[1]))) : 10
      const min = m ? Math.min(59, Math.max(0, Number(m[2]))) : 0
      const now = new Date()
      const target = new Date(now)
      target.setHours(hour, min, 0, 0)
      if (target.getTime() <= now.getTime()) {
        target.setDate(target.getDate() + 1)
      }
      return target.getTime()
    },
    secondsUntilTarget(hhmm) {
      return Math.max(0, Math.round((this.resolveTargetAt(hhmm) - Date.now()) / 1000))
    },
    syncWallclockRemaining() {
      if (!this.targetAt) this.targetAt = this.resolveTargetAt(this.effectiveConfig.targetTime || '10:00')
      this.remaining = Math.round((this.targetAt - Date.now()) / 1000)
    },
    armWallclock(start = true) {
      this.targetAt = this.resolveTargetAt(this.effectiveConfig.targetTime || '10:00')
      this.goHoldUntil = 0
      this.syncWallclockRemaining()
      if (start) this.ensureWallclockRunning()
    },
    tick() {
      if (this.mode === 'wallclock') {
        const prev = this.remaining
        this.syncWallclockRemaining()
        if (prev > 0 && this.remaining <= 0) {
          this.goHoldUntil = Date.now() + 4000
        }
        if (!this.allowOvertime && this.remaining < 0) {
          this.remaining = 0
        }
        if (this.goHoldUntil && Date.now() >= this.goHoldUntil) {
          this.goHoldUntil = 0
        }
        return
      }
      this.remaining -= 1
      if (this.remaining === 0) {
        this.goHoldUntil = Date.now() + 4000
      }
      if (this.remaining < 0 && !this.allowOvertime) {
        this.remaining = 0
        this.pause()
        return
      }
      if (this.goHoldUntil && Date.now() >= this.goHoldUntil) {
        this.goHoldUntil = 0
      }
    },
    ensureWallclockRunning() {
      if (this.mode !== 'wallclock') return
      if (this.running) return
      this.running = true
      this.timer = setInterval(this.tick, 1000)
    },
    start() {
      if (this.usesService) {
        this.startServiceCountdown()
        return
      }
      if (this.running) return
      this.running = true
      this.timer = setInterval(this.tick, 1000)
    },
    pause() {
      if (this.usesService) {
        this.pauseServiceCountdown()
        return
      }
      this.running = false
      clearInterval(this.timer)
      this.timer = null
    },
    toggle() {
      if (this.isRunning) this.pause()
      else this.start()
    },
    reset() {
      if (this.usesService) {
        this.resetServiceCountdown()
        this.goHoldUntil = 0
        return
      }
      this.pause()
      this.remaining = this.initialSeconds()
      this.goHoldUntil = 0
    },
  },
}
</script>

<style scoped>
.tminus {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 0.35rem;
  color: var(--ws-text);
  border-radius: 8px;
  padding: 0.5rem;
}
.tminus__label {
  font-size: 0.8em;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  opacity: 0.7;
}
.tminus__display {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 0.15rem;
  font-variant-numeric: tabular-nums;
  line-height: 1;
}
.tminus__prefix {
  font-size: calc(var(--ws-display-size, 32px) * 0.55);
  font-weight: 800;
  letter-spacing: -0.02em;
  opacity: 0.85;
}
.tminus__time {
  font-size: var(--ws-display-size, 32px);
  font-weight: 800;
  letter-spacing: -0.03em;
}
.tminus__go {
  font-size: calc(var(--ws-display-size, 32px) * 0.9);
  font-weight: 900;
  letter-spacing: 0.08em;
  color: #ef6e6e;
  animation: tminus-pulse 1s ease-in-out infinite;
}
.tminus__controls {
  margin-top: 0.35rem;
}
.tminus__controls .btn {
  color: var(--ws-text);
  border-color: var(--ws-panel-border);
  background: var(--ws-ctrl-bg);
  font-size: 0.85em;
}
.tminus__controls .btn-success {
  background: rgba(34, 198, 171, 0.2);
  border-color: #22c6ab;
  color: #22c6ab;
}
.tminus__controls .btn-warning {
  background: rgba(255, 188, 52, 0.2);
  border-color: #ffbc34;
  color: #ffbc34;
}
.tminus__controls .btn-secondary {
  background: var(--ws-ctrl-bg);
  border-color: var(--ws-ctrl-border);
  color: var(--ws-text);
}
.tminus--countdown .tminus__time {
  color: var(--ws-text);
}
.tminus--over .tminus__prefix,
.tminus--over .tminus__time {
  color: #22c6ab;
}
@keyframes tminus-pulse {
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.06); opacity: 0.85; }
}
</style>
